import React from 'react';
import { useTranslation } from '../../contexts/TranslationContext';

export interface PostingRhythmCell {
  dow: number;
  hour_bucket: number;
  count: number;
}

interface Props {
  data: PostingRhythmCell[];
}

const HOUR_BUCKETS = [0, 4, 8, 12, 16, 20];

const LEVEL_COLORS = [
  'rgba(88, 201, 250, 0.05)',
  'rgba(88, 201, 250, 0.20)',
  'rgba(88, 201, 250, 0.40)',
  'rgba(88, 201, 250, 0.62)',
  'rgba(88, 201, 250, 0.88)',
];

function getLevel(count: number, max: number): number {
  if (count === 0 || max === 0) return 0;
  const r = count / max;
  if (r <= 0.25) return 1;
  if (r <= 0.50) return 2;
  if (r <= 0.75) return 3;
  return 4;
}

const ExpertPostingRhythmHeatmap: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const days = t.charts.days;

  const lookup = new Map<string, number>();
  let maxCount = 0;
  for (const cell of data) {
    lookup.set(`${cell.dow}-${cell.hour_bucket}`, cell.count);
    if (cell.count > maxCount) maxCount = cell.count;
  }

  return (
    <div className="posting-rhythm">
      <div className="posting-rhythm__header">
        <span className="posting-rhythm__title">{t.charts.postingRhythm}</span>
        <div className="posting-rhythm__legend">
          <span>{t.charts.less}</span>
          {[1, 2, 3, 4].map(lvl => (
            <span
              key={lvl}
              className="posting-rhythm__legend-swatch"
              style={{ background: LEVEL_COLORS[lvl] }}
            />
          ))}
          <span>{t.charts.more}</span>
        </div>
      </div>

      <div className="posting-rhythm__grid">
        <span className="posting-rhythm__corner" />
        {days.map(day => (
          <span key={day} className="posting-rhythm__day-label">{day}</span>
        ))}
        {HOUR_BUCKETS.map(hour => (
          <React.Fragment key={hour}>
            <span className="posting-rhythm__hour-label">
              {String(hour).padStart(2, '0')}
            </span>
            {days.map((_, dow) => {
              const count = lookup.get(`${dow}-${hour}`) ?? 0;
              const level = getLevel(count, maxCount);
              const pickLabel = count !== 1 ? t.charts.picks : t.charts.pick;
              const tooltip = t.charts.heatmapTooltip
                .replace('{day}', days[dow])
                .replace('{hour}', String(hour).padStart(2, '0'))
                .replace('{count}', String(count))
                .replace('{pickLabel}', pickLabel);
              return (
                <span
                  key={dow}
                  className="posting-rhythm__cell"
                  style={{ background: LEVEL_COLORS[level] }}
                  title={tooltip}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ExpertPostingRhythmHeatmap;
